import { RateConfResult, IRoute } from '@numeo/types-core'
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    Card,
    Checkbox,
    FormControl,
    FormLabel,
    Input,
    Stack,
    Textarea,
    Typography,
} from '@mui/joy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import React, { useState } from 'react'
import { AddressFieldGroup } from './field-groups/AddressFieldGroup'
import { ContactFieldGroup } from './field-groups/ContactFieldGroup'
import { WeightFieldGroup } from './field-groups/WeightFieldGroup'
import { DimensionFieldGroup } from './field-groups/DimensionFieldGroup'
import { RouteStopEditor } from './field-groups/RouteStopEditor'

interface StructuredValidationFormProps {
    originalExtraction: RateConfResult
    onSubmit: (corrections: RateConfResult) => void
    onCancel: () => void
    loading: boolean
}

export const StructuredValidationForm: React.FC<StructuredValidationFormProps> = ({ originalExtraction, onSubmit, onCancel, loading }) => {
    const [formData, setFormData] = useState<RateConfResult>(originalExtraction)

    const handleSubmit = () => {
        onSubmit(formData)
    }

    const createEmptyRoute = (): IRoute => ({
        type: 'pickup',
        location: {
            city: '',
            state: '',
            zip: '',
            country: 'USA',
            street: '',
            coordinates: { type: 'Point', coordinates: [0, 0] },
        },
        distance: 0,
        date: '',
        time: '',
        appointmentNumber: null,
        facilityCode: null,
        facility: null,
    })

    const addRouteStop = () => {
        setFormData({
            ...formData,
            route: [...formData.route, createEmptyRoute()],
        })
    }

    const removeRouteStop = (index: number) => {
        setFormData({
            ...formData,
            route: formData.route.filter((_, i) => i !== index),
        })
    }

    const updateRouteStop = (index: number, updated: IRoute) => {
        const newRoute = [...formData.route]
        newRoute[index] = updated
        setFormData({ ...formData, route: newRoute })
    }

    return (
        <Card variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Typography level="h4" sx={{ mb: 2 }}>
                Edit Extracted Fields
            </Typography>

            <Stack spacing={2}>
                {/* Document ID */}
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-md">Document Information</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormControl>
                            <FormLabel>Document ID</FormLabel>
                            <Input value={formData.documentId} onChange={(e) => setFormData({ ...formData, documentId: e.target.value })} size="sm" />
                        </FormControl>
                    </AccordionDetails>
                </Accordion>

                {/* Broker */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-md">Broker Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <FormControl>
                                <FormLabel>Company Name</FormLabel>
                                <Input
                                    value={formData.broker?.companyName || ''}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            broker: { ...formData.broker!, companyName: e.target.value },
                                        })
                                    }
                                    size="sm"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>MC Number</FormLabel>
                                <Input
                                    value={formData.broker?.mcNumber || ''}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            broker: { ...formData.broker!, mcNumber: e.target.value || null },
                                        })
                                    }
                                    size="sm"
                                />
                            </FormControl>
                            <AddressFieldGroup
                                address={formData.broker?.address || { street: null, city: null, state: null, zip: null, country: null }}
                                onChange={(address) =>
                                    setFormData({
                                        ...formData,
                                        broker: { ...formData.broker!, address },
                                    })
                                }
                            />
                            <ContactFieldGroup
                                contact={formData.broker?.contact || { name: null, phone: null, email: null }}
                                onChange={(contact) =>
                                    setFormData({
                                        ...formData,
                                        broker: { ...formData.broker!, contact },
                                    })
                                }
                            />
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Carrier */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-md">Carrier Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <FormControl>
                                <FormLabel>Company Name</FormLabel>
                                <Input
                                    value={formData.carrier.companyName}
                                    onChange={(e) => setFormData({ ...formData, carrier: { ...formData.carrier, companyName: e.target.value } })}
                                    size="sm"
                                />
                            </FormControl>
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Route */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-md">Route ({formData.route.length} stops)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            {formData.route.map((stop, index) => (
                                <RouteStopEditor
                                    key={index}
                                    stop={stop}
                                    index={index}
                                    onChange={(updated) => updateRouteStop(index, updated)}
                                    onRemove={() => removeRouteStop(index)}
                                />
                            ))}
                            <Button variant="outlined" onClick={addRouteStop} startDecorator={<AddIcon />}>
                                Add Stop
                            </Button>
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Freight Details */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-md">Freight Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <FormControl>
                                <FormLabel>Type</FormLabel>
                                <Input
                                    value={formData.freightDetails.type}
                                    onChange={(e) => setFormData({ ...formData, freightDetails: { ...formData.freightDetails, type: e.target.value } })}
                                    size="sm"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Content</FormLabel>
                                <Input
                                    value={formData.freightDetails.content || ''}
                                    onChange={(e) =>
                                        setFormData({ ...formData, freightDetails: { ...formData.freightDetails, content: e.target.value || null } })
                                    }
                                    size="sm"
                                />
                            </FormControl>
                            <WeightFieldGroup
                                weight={formData.freightDetails.weight}
                                onChange={(weight) => setFormData({ ...formData, freightDetails: { ...formData.freightDetails, weight } })}
                            />
                            <DimensionFieldGroup
                                dimensions={formData.freightDetails.dimensions}
                                onChange={(dimensions) => setFormData({ ...formData, freightDetails: { ...formData.freightDetails, dimensions } })}
                            />
                            <Stack direction="row" spacing={1}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Quantity</FormLabel>
                                    <Input
                                        type="number"
                                        value={formData.freightDetails.quantity || ''}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                freightDetails: { ...formData.freightDetails, quantity: parseInt(e.target.value) || null },
                                            })
                                        }
                                        size="sm"
                                    />
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Total Pallets</FormLabel>
                                    <Input
                                        type="number"
                                        value={formData.freightDetails.totalPallets || ''}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                freightDetails: { ...formData.freightDetails, totalPallets: parseInt(e.target.value) || null },
                                            })
                                        }
                                        size="sm"
                                    />
                                </FormControl>
                            </Stack>
                            <FormControl>
                                <FormLabel>
                                    <Checkbox
                                        checked={formData.freightDetails.hazardous || false}
                                        onChange={(e) =>
                                            setFormData({ ...formData, freightDetails: { ...formData.freightDetails, hazardous: e.target.checked } })
                                        }
                                        label="Hazardous Material"
                                        size="sm"
                                    />
                                </FormLabel>
                            </FormControl>
                            {formData.freightDetails.hazardous && (
                                <Stack spacing={2}>
                                    <FormControl>
                                        <FormLabel>UN Number</FormLabel>
                                        <Input
                                            value={formData.freightDetails.unNumber || ''}
                                            onChange={(e) =>
                                                setFormData({ ...formData, freightDetails: { ...formData.freightDetails, unNumber: e.target.value } })
                                            }
                                            size="sm"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Hazard Class</FormLabel>
                                        <Input
                                            value={formData.freightDetails.hazardClass || ''}
                                            onChange={(e) =>
                                                setFormData({ ...formData, freightDetails: { ...formData.freightDetails, hazardClass: e.target.value } })
                                            }
                                            size="sm"
                                        />
                                    </FormControl>
                                </Stack>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Rate */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-md">Rate</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <FormControl>
                                <FormLabel>Amount</FormLabel>
                                <Input
                                    type="number"
                                    value={formData.rate.amount}
                                    onChange={(e) => setFormData({ ...formData, rate: { ...formData.rate, amount: parseFloat(e.target.value) || 0 } })}
                                    startDecorator="$"
                                    size="sm"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Currency</FormLabel>
                                <Input
                                    value={formData.rate.currency}
                                    onChange={(e) => setFormData({ ...formData, rate: { ...formData.rate, currency: e.target.value } })}
                                    size="sm"
                                />
                            </FormControl>
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* Distance */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-md">Distance</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormControl>
                            <FormLabel>Total Distance</FormLabel>
                            <Input
                                type="number"
                                value={formData.totalDistance}
                                onChange={(e) => setFormData({ ...formData, totalDistance: parseFloat(e.target.value) || 0 })}
                                endDecorator="miles"
                                size="sm"
                            />
                        </FormControl>
                    </AccordionDetails>
                </Accordion>

                {/* Notes */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-md">Additional Information</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormControl>
                            <FormLabel>Notes</FormLabel>
                            <Textarea
                                value={formData.notes || ''}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value || null })}
                                minRows={3}
                                size="sm"
                                placeholder="Add any notes or comments..."
                            />
                        </FormControl>
                    </AccordionDetails>
                </Accordion>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={onCancel} disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} loading={loading}>
                        Submit Corrections
                    </Button>
                </Box>
            </Stack>
        </Card>
    )
}
